import * as React from 'react';
import {
    FilterButton,
    SearchInput,
    SelectInput,
    FilterForm,
    ReferenceArrayInput,
    FilterList,
    FilterListItem,
    DateInput,
    AutocompleteArrayInput,
    Button,
    useListContext,
    SelectArrayInput,
} from 'react-admin';
import { Box, Stack, SxProps, Theme } from '@mui/material';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { startOfWeek, startOfMonth, subMonths, startOfToday } from 'date-fns';
import { tagInputText, tagOptionText } from '@app/tags/TagInputOption';
import {
    antennaInputText,
    antennaOptionText,
} from '@app/antenna/AntennaInputOption';
import { dateFilterInputProps } from '@core/utils/dateFilterInputProps';

export const MemberListFilter = ({
    disableSaveQuery,
    sx,
    disableAntennaFilter,
}: {
    disableSaveQuery?: boolean;
    sx?: SxProps<Theme>;
    disableAntennaFilter?: boolean;
}) => {
    const { setFilters, filterValues } = useListContext();
    const {
        memberStatusLabels,
        memberTypeLabels,
        volunteerMissionAffinityLabels,
    } = useConfigurationContext();

    const memberFilters = [
        <SearchInput
            source="search"
            alwaysOn
            placeholder="Search name, email, phone number, etc."
        />,
        !disableAntennaFilter && (
            <ReferenceArrayInput
                label="Antennas (or)"
                reference="antenna"
                source="antennaId.in"
                sort={{ field: 'name', order: 'ASC' }}
            >
                <AutocompleteArrayInput
                    label="Antennas (or)"
                    optionText={antennaOptionText}
                    inputText={antennaInputText}
                    helperText={false}
                    filterToQuery={search => ({ search })}
                />
            </ReferenceArrayInput>
        ),
        <SelectArrayInput
            source="type.in"
            label="Type (or)"
            choices={memberTypeLabels.map(type => ({
                id: type.value,
                name: type.label,
            }))}
        />,
        <SelectInput
            source="status"
            label="Status"
            choices={memberStatusLabels.map(status => ({
                id: status.value,
                name: status.label,
            }))}
        />,
        <SelectInput
            source="missionAffinity"
            label="Mission Affinity"
            choices={volunteerMissionAffinityLabels.map(missionAffinity => ({
                id: missionAffinity.value,
                name: missionAffinity.label,
            }))}
        />,
        <DateInput
            source="lastSeen.gte"
            label="Last activity after"
            {...dateFilterInputProps}
        />,
        <DateInput
            source="lastSeen.lte"
            label="Last activity before"
            {...dateFilterInputProps}
        />,
        <ReferenceArrayInput
            label="Tags (and)"
            reference="tags"
            source="tags.cs"
            sort={{ field: 'name', order: 'ASC' }}
        >
            <AutocompleteArrayInput
                label="Tags (and)"
                optionText={tagOptionText}
                inputText={tagInputText}
                filterToQuery={search => ({ search })}
            />
        </ReferenceArrayInput>,
    ].filter(Boolean);

    const hasFilters = filterValues && Object.keys(filterValues).length > 0;

    const handleClearFilters = () => {
        setFilters({});
    };

    return (
        <Box minWidth="10em" maxWidth="18em" order={-1} mr={2} sx={sx}>
            <Stack flexDirection={'row'} justifyContent={'space-between'}>
                <FilterButton
                    filters={memberFilters}
                    disableSaveQuery={disableSaveQuery}
                />
                <Button
                    disabled={!hasFilters}
                    label="Clear all"
                    onClick={handleClearFilters}
                    variant="outlined"
                    size="small"
                />
            </Stack>
            <FilterForm filters={memberFilters} />

            <FilterList label="Last activity" icon={<AccessTimeIcon />}>
                <FilterListItem
                    label="Today"
                    value={{ lastSeen: { gte: startOfToday().toISOString() } }}
                />
                <FilterListItem
                    label="This week"
                    value={{
                        lastSeen: {
                            gte: startOfWeek(new Date()).toISOString(),
                        },
                    }}
                />
                <FilterListItem
                    label="Before this week"
                    value={{
                        lastSeen: {
                            lte: startOfWeek(new Date()).toISOString(),
                        },
                    }}
                />
                <FilterListItem
                    label="Before this month"
                    value={{
                        lastSeen: {
                            lte: startOfMonth(new Date()).toISOString(),
                        },
                    }}
                />
                <FilterListItem
                    label="Before last month"
                    value={{
                        lastSeen: {
                            lte: subMonths(
                                startOfMonth(new Date()),
                                1
                            ).toISOString(),
                        },
                    }}
                />
            </FilterList>
        </Box>
    );
};
